import type {
  Profile,
  User,
  CertificateApplied,
  ProfilePageKit,
  ProfileCertificatePageKit,
  OrderPageKit,
  ProfileViewOrderPageKit,
  Social,
} from '@/models'
import { HttpErrorService } from '@/services/HttpError.service'
import { authErrorHandbook } from '@/models/Errors'
import { HttpService } from '@/services/Http.service'

export class ProfileService {
  private endpoint = '/users'
  private httpErrorService: HttpErrorService<keyof typeof authErrorHandbook>
  private httpService: HttpService

  constructor(httpService: HttpService) {
    this.httpService = httpService
    this.httpErrorService = new HttpErrorService(
      authErrorHandbook,
      'authentication',
    )
  }

  async getCurrentUser() {
    const url = [this.endpoint, 'current'].join('/')

    return await this.httpService
      .get<User>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async getUserCertificates() {
    const url = [this.endpoint, 'current/certificates'].join('/')

    return await this.httpService
      .get<CertificateApplied[]>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async updateProfile(user: Partial<Profile>) {
    const url = [this.endpoint, 'update/current'].join('/')

    return await this.httpService
      .put<Profile>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
        fetchConfig: {
          body: user,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async getProfilePageKit() {
    const url = [this.endpoint, 'profile-page-kit'].join('/')

    return await this.httpService
      .get<ProfilePageKit>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async getProfileCertificatePageKit() {
    const url = [this.endpoint, 'certificate-page-kit'].join('/')

    return await this.httpService
      .get<ProfileCertificatePageKit>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async getOrderPageKit() {
    const url = [this.endpoint, 'order-page-kit'].join('/')

    return await this.httpService
      .get<OrderPageKit>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async getViewOrderPageKit(orderId: number) {
    const url = [this.endpoint, 'orders', orderId].join('/')

    return await this.httpService
      .get<ProfileViewOrderPageKit>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
          needGlobal404: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async cancelOrder(orderId: number) {
    const url = [this.endpoint, 'orders/cancel', orderId].join('/')

    return await this.httpService
      .get<ProfileViewOrderPageKit>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
          cancellable: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async unlinkUserSocial(social: Social) {
    const url = [this.endpoint, 'unlink-social'].join('/')

    return await this.httpService
      .post(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
          cancellable: true,
        },
        fetchConfig: {
          body: {
            type: social,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async requestChangePasswordCode(credentials: {
    password: string
    passwordConfirmation: string
  }) {
    const url = [this.endpoint, 'password/change'].join('/')

    return await this.httpService
      .post(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
          cancellable: true,
        },
        fetchConfig: {
          body: {
            ...credentials,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async sendPasswordChangeCode(code: number) {
    const url = [this.endpoint, 'password/change/verify'].join('/')

    return await this.httpService
      .post(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
          cancellable: true,
        },
        fetchConfig: {
          body: {
            code,
          },
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }

  async deleteAccount() {
    const url = [this.endpoint, 'remove/account'].join('/')

    return await this.httpService
      .post<void>(url, {
        requestConfig: {
          secure: true,
          forceRequest: true,
        },
      })
      .catch((error) => {
        throw this.httpErrorService.getThrowingError(error, url)
      })
  }
}
